import {useEffect, useState} from 'react';
import {useParams, useNavigate} from "react-router";
import {
    getPurchaseOrderLineItem,
    completePurchaseOrderLine, addInventoryToCart, getProductIngredients
} from "../../api/Api";
import {Card, Checkbox, Row, Col, Table, Button, InputNumber, message, Input} from 'antd';

import {TrashWidget} from "../widget/TrashWidget";
import {ProductDisplayWidget} from "./ProductDisplayWidget";
import {UnitType, PurchaseOrderItemStatusType,  CategoryType} from "../../api/Constants";
import {toFSDateTime} from "../../util/timeutils";
import {PurchaseOrderItemStatusWidget} from "../widget/PurchaseOrderItemStatusWidget";
import {CompletedPOLineItemView} from "./CompletedPOLineItemView";
import {displayToFixedUnit, fixedToDisplayUnit} from "../../util/unitutils";
import {InventorySelectWidget} from "./InventorySelectWidget";
import {QuantityEntryWidget} from "./QuantityEntryWidget";
import {InventoryAssignmentWidget} from "./InventoryAssignmentWidget";

const {TextArea} = Input;

export const POLineItemView = ({onApiFail, refresh, onRefresh, navigateToPo=true, props}) => {
    const params = useParams();
    const purchaseId = params.purchaseId;
    const lineId = params.lineId;

    const [item, setItem] = useState(undefined);
    const [showInventory, setShowInventory] = useState(false);
    const [isBulk, setIsBulk] = useState(true);

    const [ingredients, setIngredients] = useState([]);
    const [product, setProduct] = useState(undefined);
    const [quantity, setQuantity] = useState(0);
    const [hours, setHours] = useState(undefined);
    // const [bookQuantity, setBookQuantity] = useState(0);
    const [book, setBook] = useState(true);
    const [description, setDescription] = useState(undefined);
    const [messageApi, contextHolder] = message.useMessage();
    const unit = UnitType.Each;
    const navigate = useNavigate();

    const [hintsUsed, setHintsUsed] = useState(false);
    const [ingredientHints, setIngredientHints] = useState([]);

    const getProductId = () => {
        // product is ui selectable and can be used to override the productId in the line item
        if (product) {
            return product.id;
        }
        return item.productId;
    }

    const hasProductId = () => {
        const productId = getProductId();
        return !((productId === null) || (productId === undefined));
    }

    useEffect(() => {
        if (lineId === undefined) {
            return;
        }
        getPurchaseOrderLineItem(purchaseId, lineId).then(result => {
            console.log(`item ${JSON.stringify(result)}`);
            setItem(result);
            return getProductIngredients(result.productId);
        }).then(results => {
            // we only execute this if it's the first time through
            // if we set the hints again on a subsequent i/o refresh it will
            // interfere with the form logic, and overwrite any inputs the user has entered
            if ((hintsUsed === false) && (ingredientHints.length === 0)) {
                setIngredientHints(results.map(line => {
                    return {...line, key: line.ingredientId}
                }))
            }
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

    }, [onApiFail, refresh, purchaseId, lineId, hintsUsed, ingredientHints.length]);

    const onOpenBulkInventory = () => {
        setShowInventory(true);
        setIsBulk(true);
    }

    const onOpenSupplyInventory = () => {
        setShowInventory(true);
        setIsBulk(false);
    }

    const onCloseInventory = () => {
        setShowInventory(false);
    }

    const onIngredientDelete = (index) => () => {
        const nextIngredients = [];
        for (let i=0; i<ingredients.length; i++) {
            if (i !== index) {
                nextIngredients.push(ingredients[i]);
            }
        }
        setIngredients(nextIngredients);
    }

    const selectIngredient = (ingredient, quantity=0) => {
        console.log(`ingredient ${ingredient.id} selected`);
        for (let i=0; i<ingredients.length; i++) {
            if (ingredients[i].id === ingredient.id) {
                return;
            }
        }
        const nextIngredients = [...ingredients];
        nextIngredients.push({
            ...ingredient,
            available: ingredient.quantity,
            quantity: quantity,
            waste: 0
        });
        setIngredients(nextIngredients);
        setShowInventory(false);
    }

    const onIngredientQuantityChange = (index) => (value) => {
        const nextIngredients = [...ingredients];
        nextIngredients[index].quantity = value;
        setIngredients(nextIngredients);
    }

    const onIngredientWasteChange = (index) => (value) => {
        const nextIngredients = [...ingredients];
        nextIngredients[index].waste = value;
        setIngredients(nextIngredients);
    }

    const getUnitCost = (record) => {
        if (record.category === CategoryType.Wholesale) {
            return parseFloat(record.bulkCost);
        } else {
            if ((record.cost === null) || (record.startQuantity === null)) return null;
            let cost = parseFloat(record.cost);
            if (record.tariff) {
                cost += parseFloat(record.tariff);
            }
            if (record.shipping) {
                cost += parseFloat(record.shipping);
            }
            const unitCost = parseFloat((cost / record.startQuantity).toFixed(2));
            return unitCost;
        }
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Available',
            dataIndex: 'available',
            key: 'available',
            render: (val, record) => {
                const displayQuantity = fixedToDisplayUnit(val, record.unit);
                if (record.unit === UnitType.Each) {
                    return displayQuantity
                } else {
                    return `${displayQuantity} ${UnitType.Label[record.unit]}`
                }
            }
        },
        {
            title: 'Used',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (val, record, index) => {
                // available is fixed,  quantity and waste are display
                const maxFixed = record.available - displayToFixedUnit(record.waste, record.unit);
                const max = fixedToDisplayUnit(maxFixed, record.unit);
                return <QuantityEntryWidget title={`Quantity`} placeholder={`Quantity`} unit={record.unit} value={val} max={max} onQuantityChange={onIngredientQuantityChange(index)}/>
            }
        },
        {
            title: 'Waste',
            dataIndex: 'waste',
            key: 'waste',
            render: (val, record, index) => {
                // available is fixed,  quantity and waste are display
                const maxFixed = record.available - displayToFixedUnit(record.quantity, record.unit);
                const max = fixedToDisplayUnit(maxFixed, record.unit);
                return <QuantityEntryWidget title={`Waste`} placeholder={`Waste`} unit={record.unit} value={val} max={max} onQuantityChange={onIngredientWasteChange(index)}/>
            }
        },
        {
            title: 'Unit Cost',
            dataIndex: 'cost',
            key: 'unitcost',
            render: (val, record, index) => {
                if (record.category === CategoryType.Wholesale) {
                    return `$ ${record.bulkCost} / g`
                } else {
                    const unitCost = getUnitCost(record);
                    return `$ ${unitCost}`
                }
            }
        },
        {
            title: 'Total Cost',
            dataIndex: 'cost',
            key: 'totalcost',
            render: (val, record, index) => {
                const unitCost = getUnitCost(record);
                const cost = unitCost * (record.quantity + record.waste)
                return `$ ${cost.toFixed(2)}`
            }
        }
    ]

    if (item && item.state < PurchaseOrderItemStatusType.Complete) {
        columns.push({
            title: 'Delete',
            dataIndex: 'quantity',
            key: 'delete',
            render: (val, record, index) => {
                return <TrashWidget onDelete={onIngredientDelete(index)} />
            }
        });
    }

    const ingredientsTable = <Table dataSource={ingredients} columns={columns} pagination={false}/>

    const onAssignmentSelect = (index) => (item) => {
        console.log(item);
        const nextHints = [...ingredientHints];
        nextHints[index].item = item;
        setIngredientHints(nextHints);
    }

    const onHintDelete = (index) => () => {
        const nextHints = [];
        for (let i=0; i<ingredientHints.length; i++) {
            if (i !== index) {
                nextHints.push(ingredientHints[i]);
            }
        }
        setIngredientHints(nextHints);
    }

    const onAcceptHints = () => {
        const nextIngredients = [];
        ingredientHints.forEach(hint => {
            // multiply ratio for the currently set quantity
            const ingredientQuantity = Math.floor(quantity * hint.ratio);
            console.log(`hint ${item.quantity} ${hint.ratio}, ${ingredientQuantity}`)
            if (hint.item) {
                nextIngredients.push({
                    ...hint.item,
                    available: hint.item.quantity,
                    quantity: ingredientQuantity,
                    waste: 0
                });
            }
        })
        setIngredientHints([]);
        setIngredients(nextIngredients);
        setHintsUsed(true);
    }

    const hintColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Select Inventory',
            dataIndex: 'ingredientId',
            key: 'SelectInventory',
            render: (val, record, index) => {
                // compute the quantity as the form's `quantity` * this hint's ratio
                // if it's a wholesale product use the display quantity
                const hintUnit = record.category === CategoryType.Wholesale ? UnitType.Gram : UnitType.Each;
                const displayQuantity = displayToFixedUnit(record.ratio, hintUnit) * quantity;
                return <InventoryAssignmentWidget productId={record.ingredientId} refresh={refresh} onRefresh={onRefresh}
                          onApiFail={onApiFail} onSelect={onAssignmentSelect(index)} minQuantity={displayQuantity}/>
            }
        },
        {
            title: "Ratio",
            dataIndex: 'ratio',
            key: 'ratio'
        },
        {
            title: 'Delete',
            dataIndex: 'name',
            key: 'delete',
            render: (val, record, index) => {
                return <TrashWidget onDelete={onHintDelete(index)} />
            }
        }
    ];

    const onProductSelect = (item) => {
        setProduct(item);
    }

    const onQuantityChange = (val) => {
        setQuantity(val);
    }

    const onHoursChange = (val) => {
        setHours(val);
    }

    const onBookChange = (e) => {
        setBook(e.target.checked)
    }

    const onDescriptionChange = (e) => {
        setDescription(e.target.value);
    }

    const onCompleteLineItem = () => {
        // reserve all ingredients
        const promises = [];
        ingredients.forEach(ing => {
            const fixedQuantity = displayToFixedUnit(ing.quantity, ing.unit) + displayToFixedUnit(ing.waste, ing.unit);
            promises.push(addInventoryToCart(ing.id, fixedQuantity));
        })
        Promise.all(promises).then(r => {
            // submit all the data to backend
            // const cost = ingredients.reduce((acc, ingredient) => {
            //     const unitCost = getUnitCost(ingredient);
            //     const cost = unitCost * (ingredient.quantity + ingredient.waste)
            //     return acc + cost;
            // }, 0)
            // rewrite 1.09.2026 to prevent condition where one ingredients cost is undefined
            let cost = 0;
            console.log(JSON.stringify(ingredients));
            ingredients.forEach(ingredient => {
                const unitCost = getUnitCost(ingredient);
                if (typeof unitCost === "number") {
                    cost += unitCost * (ingredient.quantity + ingredient.waste)
                }
            })

            console.log(`total cost for new item ${cost}`);
            const sku = `${item.name}-${toFSDateTime(new Date())}`

            const fixedIngredients = ingredients.map(ing => {
                return {
                    inventoryId: ing.id,
                    quantity: displayToFixedUnit(ing.quantity, ing.unit),
                    waste: displayToFixedUnit(ing.waste, ing.unit)
                }
            })
            return completePurchaseOrderLine(purchaseId, lineId, fixedIngredients, getProductId(), quantity, unit, sku, cost, hours, book, description);
        }).then(r => {
            if (navigateToPo === true) {
                navigate(`/purchaseorder/${purchaseId}`);
            } else {
                messageApi.open({
                    type: 'success',
                    content: "Items Successfully Created"
                });
                // clear the ingredients
                setIngredients([]);
                if (onRefresh) onRefresh();
            }
        }).catch(e => {
            messageApi.open({
                type: 'error',
                content: "Failed to Create Purchase Order Item"
            });
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        }).finally(() => {
            // clear the cart when we are done
        });
    }



    if (item === undefined) {
        return <div />
    }

    if (item.state === PurchaseOrderItemStatusType.Complete) {
        return <CompletedPOLineItemView item={item} onApiFail={onApiFail} onRefresh={onRefresh} refresh={refresh} />
    }

    const rowStyle={textAlign: 'left', marginTop: '5px', marginBottom: '5px'}
    const colStyle = {marginLeft: '5px', marginRight: '5px'}
    let emptyIngredient = false;
    for (let i=0; i<ingredients.length; i++) {
        if (ingredients[i].quantity === 0) {
            emptyIngredient = true;
        }
    }

    const okDisabled = (ingredients.length <= 0) || (emptyIngredient)
        || (quantity <= 0) || (getProductId() === undefined)

    const ingredientsEmpty = ingredients.length === 0;
    const hoursInvalid = (hours === null) || (hours === undefined);

    const inventoryCategory = (isBulk) ? CategoryType.Wholesale : CategoryType.Supplies;

    const hintTable = <Table dataSource={ingredientHints} columns={hintColumns} pagination={false} />
    const hintContent = <div>
        <Row style={rowStyle}>
            <b>Suggested Ingredients</b>
        </Row>
        <Row style={rowStyle}>
            {hintTable}
        </Row>
        <Row style={rowStyle}>
            <Button onClick={onAcceptHints}>Select Hints</Button>
        </Row>
    </div>

    return <div style={{textAlign: 'left'}}>
        {contextHolder}
        <Card title={'Item to Create'}>
            <Row style={rowStyle}>
                <Col span={2}><b>Requested:</b></Col>
                <Col>{item.quantity} <b>{item.name}</b></Col>
            </Row>
            <Row style={rowStyle}>
                <Col><PurchaseOrderItemStatusWidget item={item} onApiFail={onApiFail} onRefresh={onRefresh} isFulfillment={true} /></Col>
            </Row>
        </Card>
        <Card title={<div>
            <Row style={{fontSize: '16px'}}>Created Inventory</Row>
            {((quantity === 0) || (quantity === null)) &&
                <Row style={{color: 'red'}}>Step 1: Enter the Product and Quantity to Add to Inventory</Row>}
        </div>}>
            <ProductDisplayWidget productId={getProductId()} onSelect={onProductSelect} refresh={refresh} onApiFail={onApiFail} />
            <Row style={rowStyle}>
                <Col span={6}><InputNumber min={0} addonBefore={<b>Total Quantity</b>} placeholder={"Quantity"} onChange={onQuantityChange} /></Col>
            </Row>
            {(quantity > 0) && <Row style={rowStyle}>
                <Col span={4}>
                    <Checkbox onChange={onBookChange} checked={book}><b>Book</b></Checkbox>
                </Col>
                <Col span={12} style={{color: 'red'}}>
                    {book ? `You will be required to add a labor inventory item in a future version` : `Off-book`}
                </Col>
            </Row>}
        </Card>

        {quantity > 0 &&
        <Card title={
            <div>
                <Row style={{fontSize: '16px'}}>Search</Row>
                {(ingredients.length === 0) &&
                    <Row style={{color: 'red'}}>Step 2: Select the Ingredients from Inventory</Row>
                }
            </div>
        }>
            { (ingredientHints.length > 0) && <Row style={rowStyle}>
                {hintContent}
            </Row>
            }
            {!showInventory && (ingredientHints.length === 0) && <Row style={rowStyle}>
                <Col style={colStyle}>
                    <Button onClick={onOpenBulkInventory}>Select Bulk Ingredients</Button>
                </Col>
                <Col style={colStyle}>
                    <Button onClick={onOpenSupplyInventory}>Select Supplies</Button>
                </Col>
            </Row>
            }
            {showInventory && <Row style={rowStyle}>
                <Button onClick={onCloseInventory}>Close Inventory</Button>
            </Row>
            }
            {showInventory &&
            <InventorySelectWidget onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} onSelect={selectIngredient} category={inventoryCategory}/>
            }
        </Card>
        }

        {!ingredientsEmpty && <Card title={
            <div>
                <Row style={{fontSize: '16px'}}>Ingredients</Row>
                {emptyIngredient &&
                    <Row style={{color: 'red'}}>Step 3: Enter Quantity Used & Wasted for each Ingredient</Row>
                }
            </div>
        }>

            {ingredientsTable}
        </Card>}
        { !ingredientsEmpty &&!emptyIngredient &&
            <Card title={<div>
                <Row style={{fontSize: '16px'}}>Hours</Row>
                {((hours === 0) || hoursInvalid) &&
                    <Row style={{color: 'red'}}>Step 4: Enter the Number of Hours Spent</Row>}
            </div>}>
                <Row style={rowStyle}>
                    <Col><InputNumber min={0} precision={2} placeholder={"Hours"} addonAfter={`hours`} onChange={onHoursChange} /></Col>
                </Row>
            </Card>
        }
        { !ingredientsEmpty && (quantity > 0) && hasProductId() && (!hoursInvalid) &&
        <Card title={<div>
                <Row style={{fontSize: '16px'}}>Finalize</Row>
                <Row style={{color: 'red'}}>Final Step: Finalize when everything is done.</Row>
            </div>}>
            <Row style={rowStyle}>
                    <TextArea maxLength={512} rows={4} placeholder={`Inventory Description`} onChange={onDescriptionChange} />
            </Row>
            <Row style={rowStyle}>
                <Button onClick={onCompleteLineItem} disabled={okDisabled}>Complete</Button>
            </Row>
        </Card>
        }
    </div>
 }